import { ProductCategories } from "src/constants/product-categories";
import { DayjsUtils } from "src/libs/dayjs-utils";
import { Benefit } from "src/shared/models/benefit.model";
import { PgaPolicy } from "src/shared/models/pga-policy.model";

export class HealthAtHomeBenefit extends Benefit {
    // As defined by business (SNEATL-1086)
    private static RENTAL_HEALTH_AT_HOME_BENEFIT_MINIMUM_DATE = "2020-10-18";
    private static LIFE_HEALTH_AT_HOME_BENEFIT_MINIMUM_DATE = "2020-11-02";

    private static _lifeCodes = [
        "172",
        "173",
        "174",
        "179",
        "193",
        "194",
        "195",
        "196",
        "197",
        "205",
        "206",
        "207",
        "208",
    ];

    private static _rentalCodes = ["822", "825"];

    /**  @param {string} dateString  Param `dateString` should be of format `yyyy-MM-dd`  */
    private static _getDateObjectFromString(dateString: string): {
        year: number;
        month: number;
        day: number;
    } {
        const [year, month, day] = dateString
            .split("-")
            .map((s) => parseInt(s, 10));
        return { year, month, day };
    }

    private static _isAllowedPolicy(policy: PgaPolicy): boolean {
        return [
            ...HealthAtHomeBenefit._rentalCodes,
            ...HealthAtHomeBenefit._lifeCodes,
        ].includes(policy.product?.code as string);
    }

    private static _isPolicyInForce(policy: PgaPolicy): boolean {
        const meetStart = DayjsUtils.getDate(new Date(policy.validityStart));
        const meetEnd = DayjsUtils.getDate(new Date(policy.validityEnd));
        const now = DayjsUtils.getDate(new Date());

        return (
            DayjsUtils.isSameOrBefore(meetStart, now) &&
            DayjsUtils.isSameOrAfter(meetEnd, now)
        );
    }

    // Certain benefits only apply if policy was active after a date
    private static _doesPolicyExistAfterBenefitStartingDate(
        policy: PgaPolicy,
        dateString: string
    ): boolean {
        const { year, month, day } =
            HealthAtHomeBenefit._getDateObjectFromString(dateString);

        const meetMininum = DayjsUtils.getDate(new Date(year, month - 1, day));
        const meetStart = DayjsUtils.getDate(new Date(policy.validityStart));

        return DayjsUtils.isSameOrBefore(meetMininum, meetStart, "day");
    }

    // Policy benefit should only last 1 year
    private static _isPolicyBenefitInTimeFrame(policy: PgaPolicy) {
        const meetStart = DayjsUtils.getDate(new Date(policy.validityStart));
        // It's subtracted 1 day for a correct year validation
        const meetLimit = meetStart.add(1, "years").subtract(1, "day");
        const now = DayjsUtils.getDate(new Date());

        return Math.ceil(DayjsUtils.diff(now, meetLimit, "years", true)) < 1;
    }

    private static _isPolicyBenefitValid(policy: PgaPolicy): boolean {
        let doesPolicyExistsAfterBenefitStartingDate = false;
        const { category, code } = policy.product ?? {};
        const isRentalPolicy = category === ProductCategories.RENTAS;
        const isLifePolicy = category === ProductCategories.VIDA;

        if (isRentalPolicy) {
            doesPolicyExistsAfterBenefitStartingDate =
                HealthAtHomeBenefit._rentalCodes.includes(code ?? "") &&
                HealthAtHomeBenefit._doesPolicyExistAfterBenefitStartingDate(
                    policy,
                    HealthAtHomeBenefit.RENTAL_HEALTH_AT_HOME_BENEFIT_MINIMUM_DATE
                );
        }
        if (isLifePolicy) {
            doesPolicyExistsAfterBenefitStartingDate =
                HealthAtHomeBenefit._lifeCodes.includes(code ?? "") &&
                HealthAtHomeBenefit._doesPolicyExistAfterBenefitStartingDate(
                    policy,
                    HealthAtHomeBenefit.LIFE_HEALTH_AT_HOME_BENEFIT_MINIMUM_DATE
                );
        }

        return (
            HealthAtHomeBenefit._isPolicyBenefitInTimeFrame(policy) &&
            doesPolicyExistsAfterBenefitStartingDate
        );
    }

    static hasAccess(policies: PgaPolicy[]): boolean {
        const clazz = HealthAtHomeBenefit;
        return policies.some((policy) => {
            const isAllowedPolicy = clazz._isAllowedPolicy(policy);
            const isPolicyInForce = clazz._isPolicyInForce(policy);
            const isValidForBenefit = clazz._isPolicyBenefitValid(policy);

            return isAllowedPolicy && isPolicyInForce && isValidForBenefit;
        });
    }
}
